import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import ExportButtonMultipleGrid from 'components/ExportButton/ExportButtonMultipleGrid';
import {
    BUDGET_BALANCE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import { BUDGET_BALANCE_TABLES } from './constants';
import BudgetBalanceTable from './BudgetBalanceTable';
import { useDispatch } from 'store/hooks';

type Props = {
    budgetProposals: BudgetProposalT[];
    referenceYear: number;
    budgetName: string | null | undefined;
};

const StyTitle = styled(Box, {
    name: 'StyTitle',
})({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
});

const StyRefreshButton = styled(Button, {
    name: 'StyRefreshButton',
})(({ theme }) => ({
    height: '26px',
    minHeight: '26px',
    [theme.breakpoints.down(725)]: {
        height: '48px',
        minHeight: '48px',
    },
}));

export default function BudgetBalance(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const { budgetProposals, referenceYear, budgetName } = props;
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const refreshData = () => {
        queryClient
            .invalidateQueries({
                queryKey: ['budget_balance_data'],
            })
            .finally(() => {
                dispatch.vision.notifyUser({
                    message: t('budgetOverview.dataRefreshed'),
                    snackType: 'success',
                });
            });
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.totals.refreshData,
        );
    };

    return (
        <>
            <StyTitle>
                <Typography variant="h5">
                    {t('budgetOverview.budgetBalance')}
                </Typography>
                <Flex flexDirection="row">
                    <ExportButtonMultipleGrid
                        fileName={getExcelFilename(BUDGET_BALANCE_PREFIX, [])}
                        event={
                            TRACKABLE_EVENTS.budget.budgetOverview.totals
                                .exportBudgetBalance
                        }
                    />
                    <StyRefreshButton variant="contained" onClick={refreshData}>
                        {t('budgetOverview.refreshData')}
                    </StyRefreshButton>
                </Flex>
            </StyTitle>
            {BUDGET_BALANCE_TABLES.map((params) => (
                <BudgetBalanceTable
                    key={params.title}
                    title={params.title}
                    budgetProposals={budgetProposals}
                    referenceYear={referenceYear}
                    expenseOrIncome={params.expenseOrIncome}
                    budgetSection={params.budgetSection}
                    budgetName={budgetName}
                    additionalIndicators={params.additionalIndicators}
                    finalIndicator={params.finalIndicator}
                />
            ))}
        </>
    );
}
